/** Sensibull-style P&L table (operator request 2026-07-27): one row per
 * leg with its entry price, LIVE premium and current P&L, plus what that
 * leg is worth at a chosen underlying target, and a totals row. Replaces
 * the old payoff table — the numbers an operator actually watches on an
 * open basket are the live ones, not a grid of hypotheticals.
 *
 * Target columns are EXACT on-expiry values computed from the leg's
 * intrinsic value at the target (no model, no grid interpolation), so a
 * target sitting exactly on a strike reads correctly. */

import { useState, type ReactElement } from "react";
import { cn } from "@/lib/cn";
import type { PayoffLegView, PayoffView } from "@/lib/payoffApi";
import { formatLevel, formatMoney, pnlTone } from "./payoffMath";

interface Row {
  leg: PayoffLegView;
  currentPnl: number | null;
  targetPrice: number;
  targetPnl: number;
}

export function PnlTable({ data }: Readonly<{ data: PayoffView }>): ReactElement {
  const first = data.spots[0] ?? 0;
  const last = data.spots[data.spots.length - 1] ?? first;
  const spot = data.spot ?? (first + last) / 2;
  const [target, setTarget] = useState<number | null>(null);
  const at = target ?? spot;
  const rows = data.legs.map((leg) => toRow(leg, at));
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <Head />
          <tbody className="font-mono text-xs">
            {rows.map((r) => (
              <LegRow key={r.leg.symbol} row={r} />
            ))}
            <TotalRow rows={rows} />
          </tbody>
        </table>
      </div>
      <TargetControl
        value={at}
        spot={spot}
        min={first}
        max={last}
        underlying={data.underlying}
        onChange={setTarget}
        onReset={() => {
          setTarget(null);
        }}
      />
      {!data.connected && (
        <div className="mt-2 text-[11px] text-ink-muted">
          <i className="ti ti-plug-off mr-1" />
          Broker not connected — LTP and current P&amp;L need live premiums.
        </div>
      )}
    </div>
  );
}

function toRow(leg: PayoffLegView, at: number): Row {
  const dir = leg.side === "BUY" ? 1 : -1;
  const intrinsic =
    leg.optionType === "CE" ? Math.max(at - leg.strike, 0) : Math.max(leg.strike - at, 0);
  return {
    leg,
    currentPnl: leg.ltp === null ? null : dir * (leg.ltp - leg.entryFill) * leg.quantity,
    targetPrice: intrinsic,
    targetPnl: dir * (intrinsic - leg.entryFill) * leg.quantity,
  };
}

function Head(): ReactElement {
  return (
    <thead className="text-left text-[10px] uppercase tracking-wider text-ink-secondary">
      <tr className="border-b border-line">
        <th className="py-1.5 pr-2">Instrument</th>
        <th className="py-1.5 pr-2 text-right">Entry</th>
        <th className="py-1.5 pr-2 text-right">LTP</th>
        <th className="py-1.5 pr-2 text-right">Current P&amp;L</th>
        <th className="py-1.5 pr-2 text-right">Target price</th>
        <th className="py-1.5 text-right">Target P&amp;L</th>
      </tr>
    </thead>
  );
}

function LegRow({ row }: Readonly<{ row: Row }>): ReactElement {
  const { leg } = row;
  const buy = leg.side === "BUY";
  return (
    <tr className="border-b border-line last:border-0">
      <td className="py-1.5 pr-2">
        <span className={buy ? "text-success" : "text-danger"}>{buy ? "+" : "−"}</span>{" "}
        {String(leg.quantity)} × {formatLevel(leg.strike)} {leg.optionType}
      </td>
      <td className="py-1.5 pr-2 text-right">{leg.entryFill.toFixed(2)}</td>
      <td className="py-1.5 pr-2 text-right">{leg.ltp === null ? "—" : leg.ltp.toFixed(2)}</td>
      <td className={cn("py-1.5 pr-2 text-right", pnlTone(row.currentPnl ?? 0))}>
        {row.currentPnl === null ? "—" : formatMoney(row.currentPnl)}
      </td>
      <td className="py-1.5 pr-2 text-right text-ink-secondary">{row.targetPrice.toFixed(2)}</td>
      <td className={cn("py-1.5 text-right", pnlTone(row.targetPnl))}>
        {formatMoney(row.targetPnl)}
      </td>
    </tr>
  );
}

function TotalRow({ rows }: Readonly<{ rows: readonly Row[] }>): ReactElement {
  // A partial mark would misstate the basket, so the current total is
  // only shown once EVERY leg has a live premium.
  const anyMissing = rows.some((r) => r.currentPnl === null);
  const current = anyMissing ? null : rows.reduce((a, r) => a + (r.currentPnl ?? 0), 0);
  const target = rows.reduce((a, r) => a + r.targetPnl, 0);
  return (
    <tr className="border-t-2 border-line font-medium">
      <td className="py-1.5 pr-2">Total</td>
      <td className="py-1.5 pr-2" />
      <td className="py-1.5 pr-2" />
      <td className={cn("py-1.5 pr-2 text-right", pnlTone(current ?? 0))}>
        {current === null ? "—" : formatMoney(current)}
      </td>
      <td className="py-1.5 pr-2" />
      <td className={cn("py-1.5 text-right", pnlTone(target))}>{formatMoney(target)}</td>
    </tr>
  );
}

/** Underlying target picker: slider across the payoff grid plus the
 * level and its % move vs spot. Reset returns to spot. */
function TargetControl({
  value,
  spot,
  min,
  max,
  underlying,
  onChange,
  onReset,
}: Readonly<{
  value: number;
  spot: number;
  min: number;
  max: number;
  underlying: string | null;
  onChange: (v: number) => void;
  onReset: () => void;
}>): ReactElement {
  const move = spot === 0 ? 0 : ((value - spot) / spot) * 100;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
      <span className="text-ink-muted">{underlying ?? "Underlying"} target</span>
      <span className="font-mono text-ink">{formatLevel(value)}</span>
      <span className={cn("font-mono", pnlTone(move))}>
        {move > 0 ? "+" : ""}
        {move.toFixed(2)}%
      </span>
      <input
        type="range"
        aria-label="Underlying target"
        className="min-w-40 flex-1 accent-[var(--accent)]"
        min={min}
        max={max}
        step={5}
        value={value}
        onChange={(e) => {
          onChange(Number(e.target.value));
        }}
      />
      <button type="button" className="text-accent hover:underline" onClick={onReset}>
        Reset
      </button>
    </div>
  );
}
